import { RecursiveRequired } from "@common/utils/types";
import {
  Account,
  AccountDetails,
  Service,
  VmrBrandingTheme,
} from "@services/account/types";
import { UserDetails, UserService } from "@services/meetings/types";

export interface Role {
  id: number;
  locked?: boolean;
  description?: string;
  rolePermissions?: RolePermissions;
  name?: string;
  summary: RoleSummary;
}

export type EditableRole = Omit<Partial<Role>, "summary">;

export interface Vmr {
  service?: Service;
  account?: Account;
  user?: User;
  brandingTheme?: VmrBrandingTheme;
  encryptedHostPin?: string;
  vmrType?: VmrType;
  name?: string;
  shared?: boolean;
  serviceProvider?: string;
  externalOwnerId?: string;
  encryptedGuestPin?: string;
  guestLayout?: VmrLayout;
  hostLayout?: VmrLayout;
  aliases?: Array<VmrAlias>;
  personal: boolean;
  secret?: string;
  guestPin?: string;
  hostPin?: string;
  description?: string;
  participantLimit?: number;
  id?: string;
  dynamic?: boolean;
  e164?: string;
  active: boolean;
  resourceUri?: string;
}

export interface VmrAlias {
  id?: string;
  vmr?: Vmr;
  description?: string;
  resourceUri?: string;
  alias?: string;
}

export enum VmrLayout {
  OnePlusSeven = <any>"One_Plus_Seven",
  OnePlusTwentyOne = <any>"One_Plus_TwentyOne",
  OnePlusZero = <any>"One_Plus_Zero",
  TwoPlusTwentyOne = <any>"Two_Plus_TwentyOne",
  AllEqual = <any>"All_Equal",
  SpeakerOnly = <any>"Speaker_Only",
  Telepresence = <any>"Telepresence",
  Stacked = <any>"Stacked",
  AllEqualQuarters = <any>"All_Equal_Quarters",
  AllEqualNinths = <any>"All_Equal_Ninths",
  AllEqualSixteenths = <any>"All_Equal_Sixteenths",
  AllEqualTwentyFifths = <any>"All_Equal_TwentyFifths",
  OnePlusFive = <any>"One_Plus_Five",
  OnePlusNine = <any>"One_Plus_Nine",
  Automatic = <any>"Automatic",
  OnePlusN = <any>"One_Plus_N",
}

export enum VmrType {
  Conference = <any>"Conference",
  Auditorium = <any>"Auditorium",
}

export type AuthenticatedUser = RecursiveRequired<User>;

export interface UserVmr extends User {
  vmrs?: Array<Vmr>;
}

export interface User {
  id: string;
  lastName?: string;
  logoId?: number;
  role?: Role;
  firstName?: string;
  account?: Account;
  canChangePassword?: boolean;
  serviceDetails?: Array<UserService>;
  conferenceService?: Service;
  lastLogin?: number;
  email?: string;
  services?: Array<Service>;
  active: boolean;
  details?: UserDetails;
  logo?: string;
  locked?: boolean;
  accounts?: Account[];
  author?: boolean;
}

export interface RoleSummary {
  totalUsers?: number;
  roleId?: number;
}

export interface RolePermissions {
  id?: number;
  assignAuthorToUsers?: boolean;
  assignRolesToUsers?: boolean;
  rolesCapabilities?: AccessLevel;
  locations?: AccessLevel;
  accounts?: AccessLevel;
  analyticsReports?: AccessLevel;
  meetingsElogsAndRevisions?: AccessLevel;
  accountConfiguration?: AccessLevel;
  rooms?: AccessLevel;
  admin: boolean;
  adminAccountsVisibility?: AccountsVisibility;
  meetings?: AccessLevel;
  linkAccountsToUsers?: boolean;
  reports?: AccessLevel;
  users?: AccessLevel;
  role?: Role;
  tickets?: AccessLevel;
  produceMeetings?: boolean;
  assignProducer?: boolean;
  devices?: AccessLevel;
  allowBulkChanges?: boolean;
  globalConfiguration?: AccessLevel;
  attachments?: AccessLevel;
  controlDevices?: boolean;
}

export type AccessLevel = "NoAccess" | "ViewOnly" | "FullAccess";

export enum AccountsVisibility {
  AllAccounts = "AllAccounts",
  HomeTenantAccounts = "HomeTenantAccounts",
  HomeAccountOnly = "HomeAccountOnly",
}

export interface UserFilters {
  "details.userName"?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  "role.id"?: string;
  accountId?: string[];
  author?: boolean;
}

export type UserToAdd = Pick<
  User,
  "email" | "firstName" | "lastName" | "active" | "role"
> & {
  details: Pick<
    UserDetails,
    | "password"
    | "phone"
    | "mobile"
    | "address1"
    | "address2"
    | "city"
    | "country"
    | "stateOrProvince"
    | "postalCode"
    | "timeZone"
    | "title"
    | "notes"
    | "department"
    | "supervisor"
    | "billingCode"
    | "moderationCode"
    | "preferredLanguage"
    | "visibleAccountIds"
  >;
  account: Pick<AccountDetails, "id">;
};

export type UserSelfActivation = {
  email: string;
  sendWelcomeEmail: boolean;
  accountId: string;
};
